/**
 * 项目元素标签 React Query Hooks
 */
import { useCallback } from 'react';
import {
  replaceEqualDeep,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { projectElementTagsApi } from '@/services/api/project-element-tags';
import {
  cancelElementDetailQueriesByType,
  getElementDetailQueriesDataByType,
} from './useProjectElementsDetail';
import {
  cancelElementTagUpdateInFlightQueries,
  invalidateElementTagMutationCaches,
  invalidateDefaultTagMutationCaches,
  invalidateElementTagCreatedCaches,
  invalidateElementTagDeletedCaches,
  reconcileElementTagUpdateQueries,
  resolveElementTagMutationInvalidationScope,
  syncUpdatedProjectElementTagCaches,
  type ProjectResourceScope,
  type QueryRefetchType,
} from '@/lib/project-resource-cache-invalidation';
import { useCanvasAssetAssociationAttentionStore } from '@/stores/canvasAssetAssociationAttentionStore';
import { syncProjectElementTagDependentCaches } from '@/lib/project-element-tag-cache-sync';
import type {
  CreateProjectElementTagRequest,
  UpdateProjectElementTagRequest,
  SetSelectedResourceRequest,
  SetTagCoverImageRequest,
  SetDefaultTagRequest,
  ElementType,
  ProjectElementTagResponse,
} from '@/types/project-element-tag';
import type {
  CharacterDetailResponse,
  LocationDetailResponse,
  PropDetailResponse,
} from '@/services/api/project-elements';

/**
 * Query Keys
 */
export const projectElementTagsKeys = {
  all: ['project-element-tags'] as const,
  project: (projectId: string) => [...projectElementTagsKeys.all, projectId] as const,
  projectByType: (projectId: string, elementType?: ElementType) =>
    [...projectElementTagsKeys.project(projectId), elementType] as const,
  element: (projectId: string, elementType: ElementType, elementId: string) =>
    [...projectElementTagsKeys.project(projectId), elementType, elementId] as const,
};

export function applyDefaultTagSelectionToTags(
  current: ProjectElementTagResponse[] | undefined,
  elementType: ElementType,
  elementId: string,
  defaultTagId: string | null | undefined
): ProjectElementTagResponse[] | undefined {
  if (!current) return current;
  let changed = false;
  const next = current.map((tag) => {
    if (tag.elementType !== elementType || tag.elementId !== elementId) return tag;
    const isDefault = Boolean(defaultTagId && tag.tagId === defaultTagId);
    if (tag.isDefault === isDefault) return tag;
    changed = true;
    return { ...tag, isDefault };
  });
  return changed ? next : current;
}

type ElementTagMutationCacheOptions = {
  elementTagsRefetchType?: QueryRefetchType;
};

function syncUpdatedElementTagQueries(
  queryClient: QueryClient,
  result: ProjectElementTagResponse,
  data: UpdateProjectElementTagRequest
): void {
  queryClient.setQueriesData<ProjectElementTagResponse[]>(
    { queryKey: projectElementTagsKeys.all },
    (current) => {
      if (!current) return current;

      let changed = false;
      const next = current.map((tag) => {
        if (tag.tagId !== result.tagId) return tag;

        let patchedTag = tag;
        if ('tagLabel' in data) {
          patchedTag = { ...patchedTag, tagLabel: result.tagLabel };
        }
        if ('description' in data) {
          patchedTag = { ...patchedTag, description: result.description };
        }
        if ('metaData' in data) {
          patchedTag = { ...patchedTag, metaData: result.metaData };
        }
        if ('displayOrder' in data) {
          patchedTag = { ...patchedTag, displayOrder: result.displayOrder };
        }

        const tagWithUpdatedFields = replaceEqualDeep(tag, patchedTag);
        if (tagWithUpdatedFields === tag) return tag;

        const nextTag = replaceEqualDeep(tag, {
          ...tagWithUpdatedFields,
          updatedAt: result.updatedAt,
        });
        if (nextTag !== tag) changed = true;
        return nextTag;
      });
      return changed ? next : current;
    }
  );
}
/**
 * 右侧 AI 面板用子项目 projectId、预览/上传用父项目 ID 查标签时，queryKey 不一致。
 * 按 elementType + elementId（分镜 ID）失效所有缓存，保证「关联」里 scheme 缩略图立即更新。
 */
export function invalidateStoryboardImageElementTagQueriesForStoryboard(
  queryClient: QueryClient,
  storyboardId: string
): void {
  if (!storyboardId) return;
  const root = projectElementTagsKeys.all[0];
  void queryClient.invalidateQueries({
    predicate: (q) => {
      const k = q.queryKey;
      return (
        Array.isArray(k) && k[0] === root && k[2] === 'storyboard_image' && k[3] === storyboardId
      );
    },
  });
}

/**
 * 创建项目元素标签
 */
export function useCreateProjectElementTag(options?: ElementTagMutationCacheOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      data,
    }: {
      projectId: string;
      data: Omit<CreateProjectElementTagRequest, 'projectId'>;
    }) => projectElementTagsApi.createTag(projectId, data),
    onSuccess: (result, variables) => {
      invalidateElementTagCreatedCaches(queryClient, {
        permissionProjectId: variables.projectId,
        result,
        elementTagsRefetchType: options?.elementTagsRefetchType,
      });
    },
  });
}

/**
 * 获取元素的所有标签
 */
export function useElementTags(
  projectId: string | null,
  elementType: ElementType | null,
  elementId: string | null,
  options?: { enabled?: boolean; refetchOnMount?: boolean | 'always'; staleTime?: number }
) {
  const queryEnabled = (options?.enabled ?? true) && !!projectId && !!elementType && !!elementId;

  return useQuery({
    queryKey:
      projectId && elementType && elementId
        ? projectElementTagsKeys.element(projectId, elementType, elementId)
        : ['project-element-tags', 'disabled'],
    queryFn: () => {
      if (!projectId || !elementType || !elementId) {
        throw new Error('Missing required parameters');
      }
      return projectElementTagsApi.getElementTags(projectId, elementType, elementId);
    },
    enabled: queryEnabled,
    refetchOnMount: options?.refetchOnMount,
    staleTime: options?.staleTime,
  });
}

/**
 * 获取项目的所有标签
 */
export function useProjectTags(projectId: string | null, elementType?: ElementType) {
  return useQuery({
    queryKey: projectId
      ? projectElementTagsKeys.projectByType(projectId, elementType)
      : ['project-element-tags', 'disabled'],
    queryFn: () => {
      if (!projectId) {
        throw new Error('Missing projectId');
      }
      return projectElementTagsApi.getProjectTags(projectId, elementType);
    },
    enabled: !!projectId,
  });
}

/** @deprecated 请使用 invalidateElementTagMutationCaches */
export function invalidateTagSelectedResourceCaches(
  queryClient: QueryClient,
  params: {
    permissionProjectId: string;
    resourceProjectId: string;
    tagId: string;
    elementType: ElementType;
    elementId: string;
    storyboardId?: string;
  }
): void {
  invalidateElementTagMutationCaches(queryClient, params);
}

export function invalidateDefaultTagCaches(
  queryClient: QueryClient,
  params: ProjectResourceScope & {
    elementType: ElementType;
    elementId: string;
  }
): void {
  invalidateDefaultTagMutationCaches(queryClient, params);
}

/**
 * 更新项目元素标签
 */
export function useUpdateProjectElementTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      tagId,
      data,
    }: {
      projectId: string;
      tagId: string;
      data: UpdateProjectElementTagRequest;
      /** 更新标签音色后需要同步失活的分镜对白缓存（如 VoicePlayer） */
      storyboardId?: string;
    }) => projectElementTagsApi.updateTag(projectId, tagId, data),
    onSuccess: async (result, variables) => {
      const scope = resolveElementTagMutationInvalidationScope({
        mutationProjectId: variables.projectId,
        permissionProjectId: variables.data.permissionProjectId,
        responseProjectId: result.projectId,
      });
      const updateScope = {
        ...scope,
        tagId: variables.tagId,
        elementType: result.elementType,
        elementId: result.elementId,
        storyboardId: variables.storyboardId,
      };
      const cancelledQueries = await cancelElementTagUpdateInFlightQueries(
        queryClient,
        updateScope
      ).catch(() => []);
      syncUpdatedElementTagQueries(queryClient, result, variables.data);
      syncUpdatedProjectElementTagCaches(queryClient, result);
      syncProjectElementTagDependentCaches(queryClient, result);
      invalidateElementTagMutationCaches(queryClient, {
        ...scope,
        tagId: variables.tagId,
        elementType: result.elementType,
        elementId: result.elementId,
        storyboardId: variables.storyboardId,
        result,
        tagResourcesRefetchType: 'none',
      });
      void reconcileElementTagUpdateQueries(queryClient, {
        ...updateScope,
        cancelledQueries,
      });
    },
  });
}

/**
 * 删除项目元素标签
 */
export function useDeleteProjectElementTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      tagId,
    }: {
      projectId: string;
      tagId: string;
      elementType: ElementType;
      elementId: string;
      resourceProjectId?: string;
      wasDefaultTag?: boolean;
    }) => projectElementTagsApi.deleteTag(projectId, tagId),
    onSuccess: (_, variables) => {
      invalidateElementTagDeletedCaches(queryClient, {
        permissionProjectId: variables.projectId,
        resourceProjectId: variables.resourceProjectId ?? variables.projectId,
        tagId: variables.tagId,
        elementType: variables.elementType,
        elementId: variables.elementId,
        wasDefaultTag: variables.wasDefaultTag,
      });
    },
  });
}

/**
 * 设置元素的默认标签
 */
export function useSetDefaultTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      elementType,
      elementId,
      data,
    }: {
      projectId: string;
      elementType: ElementType;
      elementId: string;
      resourceProjectId?: string;
      data: SetDefaultTagRequest;
    }) => projectElementTagsApi.setDefaultTag(projectId, elementType, elementId, data),
    onMutate: async (variables) => {
      const { elementType, elementId, data } = variables;
      const newTagId = data.tagId;

      type DetailCache = CharacterDetailResponse | LocationDetailResponse | PropDetailResponse;
      type PreviousTagEntry = [QueryKey, ProjectElementTagResponse[] | undefined];

      // storyboard_image 类型不涉及元素详情缓存，直接跳过
      if (elementType !== 'character' && elementType !== 'location' && elementType !== 'prop') {
        return { previousDetail: undefined, matchedKey: undefined, previousTagEntries: [] };
      }

      // 取消指定类型的所有进行中请求（跨 projectId）
      await cancelElementDetailQueriesByType(queryClient, elementType);
      await queryClient.cancelQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === projectElementTagsKeys.all[0] &&
          Array.isArray(query.state.data),
      });

      const previousTagEntries = queryClient.getQueriesData<ProjectElementTagResponse[]>({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === projectElementTagsKeys.all[0] &&
          Array.isArray(query.state.data),
      }) as PreviousTagEntry[];

      queryClient.setQueriesData<ProjectElementTagResponse[]>(
        {
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === projectElementTagsKeys.all[0] &&
            Array.isArray(query.state.data),
        },
        (current) => applyDefaultTagSelectionToTags(current, elementType, elementId, newTagId)
      );

      // 找到缓存中匹配 elementId 的具体 detail 条目（跨 projectId 遍历匹配）
      const allCacheEntries = getElementDetailQueriesDataByType<DetailCache>(
        queryClient,
        elementType
      );
      const matchedEntry = allCacheEntries.find(([, data]) => {
        if (!data) return false;
        if (elementType === 'character')
          return (data as CharacterDetailResponse).characterId === elementId;
        if (elementType === 'location')
          return (data as LocationDetailResponse).locationId === elementId;
        return (data as PropDetailResponse).propId === elementId;
      });

      if (matchedEntry) {
        const [matchedKey, previousDetail] = matchedEntry;
        queryClient.setQueryData<DetailCache>(matchedKey, {
          ...previousDetail!,
          defaultTagId: newTagId,
        });
        return { previousDetail, matchedKey, previousTagEntries };
      }

      return { previousDetail: undefined, matchedKey: undefined, previousTagEntries };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousDetail && context?.matchedKey) {
        queryClient.setQueryData(context.matchedKey, context.previousDetail);
      }
      for (const [key, data] of context?.previousTagEntries ?? []) {
        queryClient.setQueryData(key, data);
      }
    },
    onSettled: (_, __, variables, context) => {
      const { projectId, elementType, elementId } = variables;
      const matchedKey = context?.matchedKey;
      const resourceProjectIdFromCache =
        matchedKey && typeof matchedKey[3] === 'string' ? matchedKey[3] : null;

      invalidateDefaultTagCaches(queryClient, {
        permissionProjectId: projectId,
        resourceProjectId: variables.resourceProjectId ?? resourceProjectIdFromCache ?? projectId,
        elementType,
        elementId,
      });
    },
  });
}

/**
 * 设置标签的定稿素材
 */
export function useSetTagSelectedResource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      tagId,
      data,
    }: {
      projectId: string;
      tagId: string;
      data: SetSelectedResourceRequest;
      /** 更新定稿后需同步失活的分镜对白缓存（可选） */
      storyboardId?: string;
      /** 调用方已知该标签会影响元素封面时，强制刷新资产展示列表 */
      refreshElementPresentation?: boolean;
    }) => projectElementTagsApi.setSelectedResource(projectId, tagId, data),
    onMutate: (variables) => {
      useCanvasAssetAssociationAttentionStore.getState().noteLocalTagChange(variables.tagId);
    },
    onSuccess: (result, variables) => {
      const scope = resolveElementTagMutationInvalidationScope({
        mutationProjectId: variables.projectId,
        permissionProjectId: variables.data.permissionProjectId,
        responseProjectId: result.projectId,
      });
      invalidateElementTagMutationCaches(queryClient, {
        ...scope,
        tagId: variables.tagId,
        elementType: result.elementType,
        elementId: result.elementId,
        storyboardId: variables.storyboardId,
        result,
        projectElementsListRefetchType: 'none',
        resourceLibraryRefetchType: variables.refreshElementPresentation ? 'active' : undefined,
      });
    },
  });
}

/**
 * 设置标签封面图
 */
export function useSetTagCoverImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      tagId,
      data,
    }: {
      projectId: string;
      tagId: string;
      permissionProjectId?: string;
      data: SetTagCoverImageRequest;
      /** 调用方已知该标签会影响元素封面时，强制刷新资产展示列表 */
      refreshElementPresentation?: boolean;
    }) => projectElementTagsApi.setTagCoverImage(projectId, tagId, data),
    onSuccess: (result, variables) => {
      const scope = resolveElementTagMutationInvalidationScope({
        mutationProjectId: variables.projectId,
        permissionProjectId: variables.permissionProjectId,
        responseProjectId: result.projectId,
      });
      invalidateElementTagMutationCaches(queryClient, {
        ...scope,
        tagId: variables.tagId,
        elementType: result.elementType,
        elementId: result.elementId,
        result,
        projectElementsListRefetchType: 'none',
        resourceLibraryRefetchType: variables.refreshElementPresentation ? 'active' : undefined,
      });
    },
  });
}

/**
 * 获取分镜图片方案标签列表（便捷 hook）
 * effectiveProjectId 必须是父级项目 ID（currentProject?.parentProjectId ?? currentProject?.id）
 */
export function useStoryboardImageTags(
  effectiveProjectId: string | null,
  storyboardId: string | null
) {
  return useElementTags(effectiveProjectId, 'storyboard_image', storyboardId);
}

/**
 * 失活指定项目所有元素标签相关缓存。
 */
export function invalidateProjectElementTags(queryClient: QueryClient, projectId: string) {
  return queryClient.invalidateQueries({ queryKey: projectElementTagsKeys.project(projectId) });
}

export function useInvalidateProjectElementTags() {
  const queryClient = useQueryClient();

  return useCallback(
    async (projectId: string) => {
      await invalidateProjectElementTags(queryClient, projectId);
    },
    [queryClient]
  );
}

export function invalidateAllProjectElementTags(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: projectElementTagsKeys.all });
}
